import { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { useAppWindowFlowShellStore } from 'src/app/router/layouts/navigation/app-window-frame-business'
import { paths } from 'src/config/paths'
import useDissolveScreen from 'src/features/business-create/hooks/use-dissolve-screen'
import { logger } from 'src/lib/logger'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/utils/cn'

import type { EnterpriseBusinessImportContinueResponse } from '../api/use-enterprise-business-import-continue'
import useEnterpriseBusinessImportContinue from '../api/use-enterprise-business-import-continue'
import FailedUploadModal from '../components/failed-upload-modal'
import ULFooter from '../components/ul-footer'
import ULHeader from '../components/ul-header'
import UploadLeadsNavbar from '../components/ul-navbar'
import { UL_INITIAL_STEP, UPLOAD_LEADS_EXIT_PATH, type ULStepName } from '../constants/ul-config'
import type { UploadLeadsFooterConfig } from '../context/upload-leads-context'
import { UploadLeadsContext } from '../context/upload-leads-context'
import useUploadLeadsConfig from '../hooks/use-upload-leads-config'
import { useUploadLeadsStore } from '../store/upload-leads-store'

interface ErrorModalState {
  isOpen: boolean
  data: EnterpriseBusinessImportContinueResponse
  onClose: () => void
  onRetry: () => void
}

export default function EnterpriseDashboardUploadLeadsLayout() {
  const { isMobile } = useBreakpoint()
  const { config, totalSteps } = useUploadLeadsConfig()
  const { step } = useParams()
  const { reset, getFormData } = useUploadLeadsStore()
  const { mutateAsync: continueBusinessImport } = useEnterpriseBusinessImportContinue()
  const { data: userData } = useMyUserData()
  const { setFlowShell, clearFlowShell } = useAppWindowFlowShellStore()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { setDissolve, dissolveStyles, dissolveDuration } = useDissolveScreen()
  const [footerConfig, setFooterConfig] = useState<UploadLeadsFooterConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorModalState, setErrorModalState] = useState<ErrorModalState | null>(null)
  const stepKeys = useMemo(() => Object.keys(config.steps) as ULStepName[], [config.steps])
  const currentStepKey = step as ULStepName | undefined
  const currentStepIndex = useMemo(
    () => (currentStepKey ? stepKeys.findIndex((stepKey) => stepKey === currentStepKey) : -1),
    [currentStepKey, stepKeys],
  )

  useEffect(() => {
    return () => {
      reset()
    }
  }, [])

  useEffect(() => {
    if (isMobile) {
      clearFlowShell()
      return
    }
    setFlowShell({
      content: <UploadLeadsNavbar exitLink={UPLOAD_LEADS_EXIT_PATH} />,
      width: 100,
      // Should be changed on the business page create business as well. TODO: Make this reusable.
      headerHeight: 10,
    })
  }, [clearFlowShell, isMobile, setFlowShell])

  useEffect(() => {
    return () => {
      clearFlowShell()
    }
  }, [clearFlowShell])

  const handleBack = useCallback(() => {
    // Pressing the back button on the first step should navigate to the pipeline page
    if (currentStepIndex === 0) {
      navigate(UPLOAD_LEADS_EXIT_PATH)
      return
    }

    setDissolve(true)
    setTimeout(() => {
      navigate(-1)
      setDissolve(false)
    }, dissolveDuration)
  }, [dissolveDuration, navigate, setDissolve, currentStepIndex])

  const handleNext = useCallback(async () => {
    if (isLoading) return

    const formData = getFormData()
    setDissolve(true)

    const skipSteps: ULStepName[] = []
    // Logic to skip the reupload step.
    if (formData.closedDeals === false || (formData.closedDeals === true && formData.uploadedFileHasClosedDate)) {
      skipSteps.push('reupload')
    }

    let targetStepIndex = currentStepIndex + 1

    while (targetStepIndex < stepKeys.length && skipSteps.includes(stepKeys[targetStepIndex])) {
      targetStepIndex++
    }

    /**
     * Calls business import continue API.
     */
    if (targetStepIndex >= stepKeys.length || targetStepIndex >= totalSteps) {
      const enterpriseId = userData?.enterpriseId
      const uploadId = formData.uploadedFile?.previewData.uploadId
      if (!enterpriseId || !uploadId) {
        setDissolve(false)
        toast({
          title: 'Could not complete import',
          description: 'Missing import context. Please re-upload the file and try again.',
          variant: 'destructive',
        })
        return
      }

      setIsLoading(true)
      try {
        const response = await continueBusinessImport({
          enterpriseId,
          uploadId,
          lynkEnabledForFile: formData.lynkEnabledForFile,
          containsClosedBusinesses: formData.closedDeals,
          distributionSystemId: formData.distributionSystemId,
          leadDistributionPreference: formData.leadDistributionPreference,
        })

        if (response.success === false) {
          throw new Error('Failed to upload leads.')
        }

        if (response.summary.failed > 0) {
          // Display error modal if there are failed imports.
          setErrorModalState({
            isOpen: true,
            data: response,
            onClose: () => navigate(UPLOAD_LEADS_EXIT_PATH),
            onRetry: () => {
              reset()
              setErrorModalState(null)
              navigate(paths.enterprise.dashboard.uploadLeads.getHref(UL_INITIAL_STEP))
            },
          })
        } else {
          toast({
            title: 'Leads uploaded',
            variant: 'default',
          })
          navigate(UPLOAD_LEADS_EXIT_PATH)
        }
      } catch (error) {
        logger.error(error)

        if (error instanceof Error) {
          if (error.message.includes('Import preview not found or expired.')) {
            toast({
              title: 'Failed to upload',
              description: error.message,
              variant: 'destructive',
            })
            return
          }
        }

        toast({
          title: 'Could not complete import',
          description: 'The import failed. Please try again.',
          variant: 'destructive',
        })
      } finally {
        setIsLoading(false)
        setDissolve(false)
      }
      return
    }

    setTimeout(() => {
      const nextStepKey = stepKeys[targetStepIndex]
      navigate(paths.enterprise.dashboard.uploadLeads.getHref(nextStepKey))
      setDissolve(false)
    }, dissolveDuration)
  }, [
    currentStepKey,
    currentStepIndex,
    continueBusinessImport,
    dissolveDuration,
    getFormData,
    isLoading,
    navigate,
    setDissolve,
    stepKeys,
    toast,
    totalSteps,
    userData?.enterpriseId,
  ])

  const handleGoToStep = useCallback(
    (targetStep: ULStepName) => {
      const targetStepIndex = stepKeys.findIndex((stepKey) => stepKey === targetStep)
      if (targetStepIndex < 0 || targetStepIndex >= totalSteps) return

      setDissolve(true)
      setTimeout(() => {
        navigate(paths.enterprise.dashboard.uploadLeads.getHref(targetStep))
        setDissolve(false)
      }, dissolveDuration)
    },
    [dissolveDuration, navigate, setDissolve, stepKeys, totalSteps],
  )

  const contextValue = useMemo(
    () => ({ handleNext, handleGoToStep, handleBack, setFooterConfig }),
    [handleBack, handleGoToStep, handleNext],
  )

  if (isMobile) {
    return (
      <UploadLeadsContext.Provider value={contextValue}>
        <div className={cn('size-full')} style={dissolveStyles}>
          <Outlet />
        </div>
      </UploadLeadsContext.Provider>
    )
  }

  return (
    <UploadLeadsContext.Provider value={contextValue}>
      <div className="relative size-full overflow-hidden">
        <ULHeader showBackButton={currentStepIndex !== 0} />
        <div className={cn('flex size-full overflow-y-auto pb-[80px]')} style={dissolveStyles}>
          <Outlet />
        </div>
        <ULFooter
          isLoading={isLoading}
          buttonText={footerConfig?.buttonText ?? 'Continue'}
          buttonVariant={footerConfig?.buttonVariant ?? 'default'}
          primaryAction={footerConfig?.primaryAction ?? (() => handleNext())}
          isPrimaryDisabled={footerConfig?.isPrimaryDisabled ?? false}
          bottomLeftContent={footerConfig?.bottomLeftContent}
        />
      </div>
      {errorModalState && (
        <FailedUploadModal
          isOpen={errorModalState.isOpen}
          setOpen={(open) => {
            if (open) {
              setErrorModalState((prev) => (prev ? { ...prev, isOpen: true } : null))
            } else {
              errorModalState.onClose()
              setErrorModalState(null)
            }
          }}
          data={errorModalState.data}
          onRetry={errorModalState.onRetry}
          onClose={errorModalState.onClose}
        />
      )}
    </UploadLeadsContext.Provider>
  )
}
